import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { SettlementStateEvent } from '@treadinc/horizon-api-spec';
import { SettlementState as SettlementStateEnum } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useRef, useState } from 'react';

import Menu, { MenuHandler } from '~components/Menu/Menu';
import {
  LoadingSpinner,
  OverflowAwareText,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { chipPropsBySettlementState } from '~components/Settlements/SettlementState';
import { FeatureFlags } from '~constants/featureFlags';
import { Settlement } from '~hooks/useSettlements/models';
import useSettlements, {
  isSettlementStateTransition,
} from '~hooks/useSettlements/useSettlements';
import { useStores } from '~store';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { getSettlementStateEventLabel } from './helpers';

interface SettlementStatusProps {
  onSettlementChange: (settlement: Settlement) => void;
  settlement: Settlement;
}

const SettlementStatusControl = ({
  onSettlementChange,
  settlement,
}: SettlementStatusProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const menuHandler = useRef<MenuHandler>(null);
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { transitionSettlementState } = useSettlements();
  const theme = useTheme();

  const hiddenStateEvents: SettlementStateEvent[] = [
    SettlementStateEvent.VOID,
    SettlementStateEvent.SEND_SETTLEMENT,
  ];

  const availableStateEvents = settlement.stateEvents.filter(
    (stateEvent) => !hiddenStateEvents.includes(stateEvent),
  );
  const { color: chipColor, label: chipLabel } =
    chipPropsBySettlementState[settlement.state];

  const doAction = async (stateEvent: SettlementStateEvent) => {
    setIsLoading(true);

    try {
      if (!isSettlementStateTransition(stateEvent)) {
        console.error(`Unknown state event: ${stateEvent}`);
        return;
      }

      onSettlementChange(
        await transitionSettlementState(companyId, String(settlement.id), stateEvent),
      );
    } catch {
      // reported by the hook
    } finally {
      setIsLoading(false);
    }
  };

  // Extracted chip rendering for reuse
  const renderStatusChip = () => (
    <Chip
      color={chipColor}
      label={chipLabel}
      size="small"
      variant="outlined"
      sx={{
        '&.MuiChip-root': {
          '&.MuiChip-outlined': { flexDirection: 'row-reverse', borderRadius: 1 },
          '.MuiChip-label': {
            fontSize: '12px',
            fontWeight: 700,
          },
          '.MuiChip-icon': { mx: 0 },
          height: '30px',
        },
      }}
    />
  );

  if (availableStateEvents.length === 0) {
    if (settlement.state === SettlementStateEnum.VOIDED && settlement.voidReason) {
      return (
        <Tooltip
          title={settlement.voidReason}
          placement="bottom"
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, -8], // [horizontal, vertical] - reduces vertical distance
                  },
                },
              ],
            },
          }}
        >
          <span>{renderStatusChip()}</span>
        </Tooltip>
      );
    }
    return renderStatusChip();
  }

  const isPaidState =
    settlement.state === SettlementStateEnum.PAID ||
    settlement.state === SettlementStateEnum.CUSTOMER_PAID;

  const isApprovedState =
    settlement.state === SettlementStateEnum.APPROVED ||
    settlement.state === SettlementStateEnum.CUSTOMER_APPROVED;

  if (availableStateEvents.length === 1 && !isPaidState && !isApprovedState) {
    return (
      <SmallLoadingButton
        loading={isLoading}
        color={'brandV2Green'}
        onClick={() => doAction(availableStateEvents[0])}
      >
        {getSettlementStateEventLabel(availableStateEvents[0])}
      </SmallLoadingButton>
    );
  }

  return (
    <Menu
      ref={menuHandler}
      disableAutoFocusItem
      menuTrigger={
        <Box sx={{ cursor: 'pointer', display: 'inline-block', height: 'fit-content' }}>
          <Chip
            color={chipColor}
            label={chipLabel}
            icon={
              <KeyboardArrowDown
                sx={{ color: theme.palette.primary.main, fontSize: '15px' }}
              />
            }
            size="small"
            variant="outlined"
            sx={{
              '&.MuiChip-root': {
                '&.MuiChip-outlined': { flexDirection: 'row-reverse', borderRadius: 1 },
                '.MuiChip-label': {
                  fontSize: '12px',
                  fontWeight: 700,
                  pr: 0,
                },
                '.MuiChip-icon': { mx: 0 },
              },
            }}
          />
        </Box>
      }
    >
      {availableStateEvents.map((stateEvent) => (
        <MenuItem
          key={stateEvent}
          onClick={(event) => {
            event.stopPropagation();
            if (!isLoading) {
              doAction(stateEvent);
            }
          }}
          disabled={isLoading}
        >
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: 1,
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <OverflowAwareText
              flex={1}
              sx={{
                color: isLoading
                  ? theme.brandV2.colors.treadGray6
                  : theme.brandV2.colors.treadBlack,
              }}
            >
              {getSettlementStateEventLabel(stateEvent)}
            </OverflowAwareText>

            <LoadingSpinner
              isVisible={isLoading}
              loadingIndicatorSize={15}
              sx={{ flex: 0, minWidth: '15px' }}
            />
          </Box>
        </MenuItem>
      ))}
    </Menu>
  );
};

const SettlementStatus = ({ onSettlementChange, settlement }: SettlementStatusProps) => {
  const isItemMatchingEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.qbItemMatchLlm,
  });
  const lineItemsNeedingItemCount = settlement.rutterLineItemsNeedingItemCount;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <SettlementStatusControl
        onSettlementChange={onSettlementChange}
        settlement={settlement}
      />
      {isItemMatchingEnabled && lineItemsNeedingItemCount > 0 && (
        <Tooltip
          title={t('approvals.driver_pay.settlements.quick_books_items_needing_item', {
            count: lineItemsNeedingItemCount,
          })}
        >
          <WarningAmberIcon
            color="warning"
            data-test-id="settlement-needs-quick-books-item-warning"
            fontSize="small"
          />
        </Tooltip>
      )}
    </Box>
  );
};

export default SettlementStatus;
